/**
 * screens/CatalogScreen.tsx
 * Setup · Intent Catalog (softknack) — global authored intent list.
 * Recreated verbatim from the design HTML (~413-439), max-width 1080.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { useTheme } from '../context/themeContext';
import Icon from '../components/icon';
import { Chip, Button, ScreenBody } from '../components/voiceUi';
import CreateIntentDrawer from '../components/CreateIntentDrawer';
import IntentEditorDrawer from '../components/IntentEditorDrawer';
import { CatalogIntent } from '../data/voiceMock';
import { listCatalog } from '../services/voice/voiceService';
import { humanize, intentTitle } from '../utils/format';

// Grid columns matching the HTML: 1.4 / 1 / 1.3 / 70 / 70 / 60 / 30 (gap 12).
const COLS = { intent: 1.4, owner: 1, spine: 1.3, enrich: 70, tenants: 70, ver: 60, chev: 30 };

export default function CatalogScreen() {
  const { theme } = useTheme();
  const c = theme.colors;

  const [filter, setFilter] = useState('all');
  const [creating, setCreating] = useState(false);
  const [editSlug, setEditSlug] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<CatalogIntent[]>([]);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, live: isLive } = await listCatalog();
    setCatalog(data);
    setLive(isLive);
    setLoading(false);
  }, []);
  // Plain effect — the console shell has no React Navigation (no useFocusEffect).
  useEffect(() => { void load(); }, [load]);

  const owners = [...new Set(catalog.map(i => i.ownerLabel))];
  const filters = [{ key: 'all', label: 'All intents' }, ...owners.map(o => ({ key: o, label: o }))];
  const rows = catalog.filter(i => filter === 'all' || i.ownerLabel === filter);

  const headStyle = { fontSize: 10.5, fontWeight: '600' as const, letterSpacing: 0.5, textTransform: 'uppercase' as const, color: c.ink4, fontFamily: theme.font.sans };
  const monoCell = { fontFamily: theme.font.mono, fontSize: 12, color: c.ink3 };

  return (
    <ScreenBody maxWidth={1080}>
      {/* Filter chip row + New intent */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {filters.map(f => {
          const active = filter === f.key;
          return (
            <Pressable key={f.key} onPress={() => setFilter(f.key)}
              style={{ height: 28, paddingHorizontal: 12, borderRadius: 20, borderWidth: 1, justifyContent: 'center', borderColor: active ? c.ink : c.line, backgroundColor: active ? c.ink : c.card }}>
              <Text style={{ fontSize: 12, fontWeight: '500', color: active ? c.paper : c.ink2, fontFamily: theme.font.sans }}>{f.label}</Text>
            </Pressable>
          );
        })}
        <View style={{ flex: 1 }} />
        {!live && !loading && (
          <Text style={{ fontSize: 11.5, color: c.ink4, fontFamily: theme.font.mono, marginRight: 4 }}>Couldn’t load — check connection</Text>
        )}
        <Button kind="primary" label="New intent" icon="plus" onPress={() => setCreating(true)} />
      </View>

      {loading && (
        <View style={{ padding: 40, alignItems: 'center' }}><ActivityIndicator color={c.ink3} /></View>
      )}

      {!loading && rows.length === 0 && (
        <View style={{ borderWidth: 1, borderStyle: 'dashed', borderColor: c.lineStrong, borderRadius: 10, padding: 36, alignItems: 'center', backgroundColor: c.paper2 }}>
          <Text style={{ fontWeight: '600', color: c.ink2, marginBottom: 4, fontFamily: theme.font.sans }}>No intents authored yet</Text>
          <Text style={{ fontSize: 12.5, color: c.ink3, textAlign: 'center', fontFamily: theme.font.sans }}>Create the first intent — it becomes available for tenants to toggle in Controls.</Text>
        </View>
      )}

      {/* Table */}
      {!loading && rows.length > 0 && (
      <View style={{ backgroundColor: c.card, borderWidth: 1, borderColor: c.line, borderRadius: 10, overflow: 'hidden', ...(theme.shadows.sm as object) }}>
        {/* Header */}
        <View style={{ flexDirection: 'row', gap: 12, paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.line, backgroundColor: c.paper2 }}>
          <Text style={[headStyle, { flex: COLS.intent }]}>Intent</Text>
          <Text style={[headStyle, { flex: COLS.owner }]}>Owner</Text>
          <Text style={[headStyle, { flex: COLS.spine }]}>Spine tool</Text>
          <Text style={[headStyle, { width: COLS.enrich }]}>Enrich</Text>
          <Text style={[headStyle, { width: COLS.tenants }]}>Tenants</Text>
          <Text style={[headStyle, { width: COLS.ver }]}>Ver</Text>
          <Text style={[headStyle, { width: COLS.chev }]}></Text>
        </View>
        {/* Rows */}
        {rows.map((i, idx) => (
          <Pressable key={i.slug} onPress={() => setEditSlug(i.slug)}
            style={{ flexDirection: 'row', gap: 12, alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: idx === rows.length - 1 ? 0 : 1, borderBottomColor: c.line }}>
            <View style={{ flex: COLS.intent }}>
              <Text style={{ fontWeight: '600', fontSize: 13, color: c.ink, fontFamily: theme.font.sans }}>{intentTitle(i)}</Text>
              <Text style={{ fontSize: 11, color: c.ink4, fontFamily: theme.font.sans }}>{i.category} · {humanize(i.slug)}</Text>
            </View>
            <View style={{ flex: COLS.owner, alignItems: 'flex-start' }}>
              <Chip kind={i.ownerKind === 'VERTICAL' ? 'outline' : 'default'}>{i.ownerLabel}</Chip>
            </View>
            <Text style={{ flex: COLS.spine, fontFamily: theme.font.mono, fontSize: 11.5, color: c.ink2 }}>{i.spine}</Text>
            <Text style={[monoCell, { width: COLS.enrich }]}>{i.enrich.length}</Text>
            <Text style={[monoCell, { width: COLS.tenants }]}>{i.tenants}</Text>
            <Text style={[monoCell, { width: COLS.ver }]}>v{i.ver}</Text>
            <View style={{ width: COLS.chev }}><Icon name="chev-r" size={15} color={c.ink4} /></View>
          </Pressable>
        ))}
      </View>
      )}

      <CreateIntentDrawer open={creating} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); void load(); }} />
      {editSlug && (
        <IntentEditorDrawer
          open
          onClose={() => setEditSlug(null)}
          slug={editSlug}
          intentId={catalog.find(i => i.slug === editSlug)?.id}
          source={catalog}
          onSaved={() => void load()}
        />
      )}
    </ScreenBody>
  );
}
